import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import cloneDeep from 'lodash.clonedeep';
import logger from '@widgets/logger';
import MenuItem from '@mui/material/MenuItem';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalizeItemModal from '@features/LocalizeItemModal';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import DragAndDrop, { SortableItem } from '@components/DragAndDrop';
import { useTranslation, useSurveysOptions } from '@hooks';
import { micrositesService } from '@services';
import { alertActions } from '@actions';
import { PREVIEW_LOCALES, LANGUAGES_MAP } from '@constants/microsites';
import { validateFileSize } from '@helpers/upload-file';
import { handleApiError } from '@helpers/api';
import { MB } from '@constants/file';
import MicrositeLink from './MicrositeLink';
import { CssMenu } from './styles';

const LinkType = { Link: 0, Survey: 1, File: 2 };

const Landing = ({ isView, locale, selectedMicrosite }) => {
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [localizeModal, setLocalizeModal] = useState({ open: false, index: null, ctx: null });
    const [anchorEl, setAnchorEl] = useState(null);

    const { setValue, watch } = useFormContext();
    const { getSurveysOptionsByLocation } = useSurveysOptions();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const locationId = watch('locationId');
    const links = watch('links');
    const availableLanguages = LANGUAGES_MAP[locale];
    const currentLocale = PREVIEW_LOCALES[locale];

    const surveysOptions = useMemo(
        () => getSurveysOptionsByLocation(locationId, s => ({ label: s.name, value: s.externalId })),
        [locationId],
    );

    const handleClick = event => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const linkClick = key => {
        const linksCopy = cloneDeep(links);
        linksCopy[key][0].isOpen = !linksCopy[key][0].isOpen;
        setValue('links', linksCopy, { shouldDirty: true });
    };

    const onClickAdd = type => {
        handleClose();
        const linksCopy = cloneDeep(links);
        const newLinks = {
            ...linksCopy,
            [Object.keys(linksCopy).length]: [
                {
                    title: t('common.title'),
                    link: '',
                    language: availableLanguages[0],
                    ...(type === LinkType.Survey && { surveyId: surveysOptions[0]?.value }),
                    ...(type === LinkType.File && { fileUrl: '' }),
                },
            ],
        };
        setValue('links', newLinks, { shouldDirty: true });
    };

    const onChangeDefaultLink = (e, key, lang) => {
        const linksCopy = cloneDeep(links);
        // INFO: find index of the link to change
        const linkIndex = linksCopy[key].findIndex(l => l.language === lang);
        // INFO: update it with necessary field
        linksCopy[key][linkIndex] = { ...linksCopy[key][linkIndex], [e.target.name]: e.target.value };
        setValue('links', linksCopy, { shouldDirty: true });
    };

    const onChangeSurvey = (e, key) => {
        const linksCopy = cloneDeep(links);
        linksCopy[key].forEach(item => {
            item.surveyId = e.target.value;
            item.link = '';
        });
        setValue('links', linksCopy, { shouldDirty: true });
    };

    const onChangeFile = async (e, key, lang) => {
        const linksCopy = cloneDeep(links);
        const linkIndex = linksCopy[key].findIndex(l => l.language === lang);
        try {
            const file = e.target.files[0];
            if (!file) return;
            const { error, metric, size } = validateFileSize(file, 15 * MB);
            if (error) {
                dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
                return;
            }
            setIsUploadingFile(true);
            const data = new FormData();
            data.append('file', file);
            data.append('fileName', file.name);
            const response = await micrositesService.uploadMicrositeFile(data, selectedMicrosite.id);
            linksCopy[key][linkIndex] = { ...linksCopy[key][linkIndex], fileUrl: response.publicUrl };
            setValue('links', linksCopy, { shouldDirty: true });
        } catch (error) {
            handleApiError(error);
            logger.error("Couldn't upload file microsite");
        } finally {
            setIsUploadingFile(false);
            e.target.value = '';
        }
    };

    const onClickDelete = key => {
        const linksCopy = cloneDeep(links);
        delete linksCopy[key];
        const copyUpdatedIndexes = Object.values(linksCopy).reduce(
            (prev, value, index) => ({ ...prev, [index]: value }),
            {},
        );
        setValue('links', copyUpdatedIndexes, { shouldDirty: true });
    };

    const dndEntries = Object.entries(links);

    return (
        <>
            <div
                style={{
                    margin: '0 0 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <div>
                    <Typography variant="body2">{t('MicrositesPage.links')}</Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', margin: '4px 0 8px' }}
                    >
                        {t('MicrositesPage.linksSubtitle')}
                    </Typography>
                </div>
                <div>
                    <Button
                        id="pl-menu-add-link"
                        variant="outlined"
                        onClick={handleClick}
                        disabled={isView}
                        startIcon={anchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}
                    >
                        {t('buttons.add')}
                    </Button>
                    <CssMenu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={handleClose}
                        sx={{ zIndex: 9999 }}
                        PaperProps={{ id: 'pl-menu-add-link-list' }}
                        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                    >
                        <MenuItem onClick={() => onClickAdd(LinkType.Link)}>
                            <Typography variant="body2">{t('common.link')}</Typography>
                        </MenuItem>
                        {surveysOptions.length ? (
                            <MenuItem onClick={() => onClickAdd(LinkType.Survey)}>
                                <Typography variant="body2">{t('common.survey')}</Typography>
                            </MenuItem>
                        ) : null}
                        <MenuItem onClick={() => onClickAdd(LinkType.File)}>
                            <Typography variant="body2">{t('common.file')}</Typography>
                        </MenuItem>
                    </CssMenu>
                </div>
            </div>
            <div style={{ marginTop: 8, marginBottom: 24 }}>
                <DragAndDrop
                    items={dndEntries.map(i => i[0])}
                    data={links}
                    onDragEndCb={result => {
                        setValue('links', result, { shouldDirty: true });
                    }}
                >
                    {dndEntries.map(([key, links]) => {
                        const link = links.find(l => l.language === currentLocale) || links[0];
                        return (
                            <SortableItem key={key} id={key}>
                                <MicrositeLink
                                    isView={isView}
                                    link={link}
                                    isOpen={links[0]?.isOpen}
                                    index={key}
                                    isUploadingFile={isUploadingFile}
                                    surveysOptions={surveysOptions}
                                    linkClick={linkClick}
                                    onChangeDefaultLink={onChangeDefaultLink}
                                    onChangeSurvey={onChangeSurvey}
                                    onChangeFile={onChangeFile}
                                    setLocalizeModal={setLocalizeModal}
                                    onClickDelete={onClickDelete}
                                />
                            </SortableItem>
                        );
                    })}
                </DragAndDrop>
            </div>
            {localizeModal.open ? (
                <LocalizeItemModal
                    modal={localizeModal}
                    onClose={() => setLocalizeModal({ open: false, index: null, ctx: null })}
                    currentLocale={currentLocale}
                    propName="link"
                    entity="links"
                    withChangeEntity={!localizeModal?.ctx?.isSurvey}
                    withUploadFile={localizeModal?.ctx?.isFile}
                    onUploadFile={data => micrositesService.uploadMicrositeFile(data, selectedMicrosite.id)}
                />
            ) : null}
        </>
    );
};

export default Landing;
